import { DownOutlined } from '@ant-design/icons';
import { WaterbodyDetail } from '@omniafishing/core';
import { Button, Collapse } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getImgixPath } from '../../../../lib/imgix';
import { OmniaUrls } from '../../../../lib/urls';
import { sqKmToAcres } from '../../../../lib/waterbody';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { getAmbassadors } from '../../../../redux/waterbody';
import { ClarityRange } from '../../../clarity_range/clarity_range';
import { WaterbodyActivation } from '../../waterbody_activation';
import { useWaterbodyWebAnalyticsArgs } from '../../waterbody_detail_lib/use_waterbody_web_analytics_args';
import styles from './waterbody_detail_learn_more.less';
import { WaterbodyDetailUserProfileCard } from './waterbody_detail_user_profile_card';

interface WaterbodyDetailLearnMoreProps {
  waterbody: WaterbodyDetail;
  defaultActiveKey?: WaterbodyDetailLearnMorePanelKeys;
}

export const waterClarity = (clarity: number) => {
  if (clarity < 4) {
    return `Clear (${clarity}/10)`;
  } else if (clarity >= 4 && clarity < 7) {
    return `Stained (${clarity}/10)`;
  } else if (clarity >= 7) {
    return `Murky (${clarity}/10)`;
  }
};

export enum WaterbodyDetailLearnMorePanelKeys {
  Description = '1',
  LakeDetails = '2',
  Species = '3',
  Ambassadors = '4',
}

export const WaterbodyDetailLearnMore = (props: WaterbodyDetailLearnMoreProps) => {
  const { waterbody, defaultActiveKey } = props;
  const {
    average_depth_ft,
    area_sqkm,
    classification,
    description,
    omnia_clarity,
    primary_name,
    secondary_title,
    species_known,
    species,
    url_slug,
  } = waterbody;
  const ambassadors = useSelector(getAmbassadors);
  const webAnalyticsLakeClickArgs = useWaterbodyWebAnalyticsArgs(waterbody);
  const [ambassadorCount, setAmbassadorCount] = useState(12);
  const { Panel } = Collapse;

  const getGenericDescription = (w: WaterbodyDetail) => {
    const speciesPresent = w.species_known
      ? w.species.map((specie) => specie.display_name).join(', ')
      : 'a variety of species';
    const currentSeasons = w.seasons
      .filter((season) => season.active)
      .map((season) => season.display_name)
      .join('/');
    const locales = w.locales.map((locale) => `${locale.state.abbr}`).join(', ');
    return `${primary_name} is a body of water in ${locales}. During this time of year, fish can be found in ${currentSeasons} patterns. Available species are ${speciesPresent}. Narrow down your tackle selection by adjusting season, water clarity, structure and technique.`;
  };

  const averageDepth = Math.round(average_depth_ft);

  const speciesWithImages = species.filter((s) => s.image);
  const speciesWithoutImages = species.filter((s) => !s.image);
  const speciesWithoutImagesCount = speciesWithoutImages.length;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>
          Learn More <span>about {primary_name}</span>
        </h2>
      </div>
      <div>
        <Collapse
          expandIconPosition="end"
          expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
          onChange={() =>
            WebAnalytics.lakePageClick('[learn_more].(accordion)', webAnalyticsLakeClickArgs)
          }
          defaultActiveKey={defaultActiveKey}
        >
          <Panel
            header={<h3>{secondary_title || `What do you need to know about ${primary_name}?`}</h3>}
            key={WaterbodyDetailLearnMorePanelKeys.Description}
          >
            <div className={styles.description}>
              {description ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              ) : (
                <p>{getGenericDescription(waterbody)}</p>
              )}
              <WaterbodyActivation waterbody={waterbody} urlSlug={url_slug} />
            </div>
          </Panel>
          <Panel
            key={WaterbodyDetailLearnMorePanelKeys.LakeDetails}
            header={<h3>What is {primary_name}'s size, depth, and clarity?</h3>}
          >
            <div className={styles.lakeStats}>
              <p>
                <strong>Type:</strong>
                {classification && classification.name}
              </p>
              <p>
                <strong>Size:</strong>
                {sqKmToAcres(area_sqkm)} acres
              </p>
              <p>
                <strong>Avg. Depth:</strong>
                {averageDepth === 0 ? 'Unknown' : Math.round(averageDepth) + 'ft.'}{' '}
              </p>
              <div>
                <strong>Water Clarity:</strong>
                {omnia_clarity != null ? (
                  <>
                    <span>{waterClarity(omnia_clarity)} </span>
                    <div className={styles.waterClarityWidget}>
                      <ClarityRange clarities={[omnia_clarity]} clarityScaleOnly />
                    </div>
                  </>
                ) : (
                  'Unknown'
                )}
              </div>
            </div>
          </Panel>
          <Panel
            header={<h3>What fish are in {primary_name}?</h3>}
            key={WaterbodyDetailLearnMorePanelKeys.Species}
          >
            {species_known ? (
              <ul className={styles.speciesList}>
                {speciesWithImages.map((specie) => (
                  <li className={styles.specie} key={specie.name}>
                    <Link to={OmniaUrls.species(specie)}>
                      <img src={getImgixPath(specie.image, { w: 260 })} alt={specie.display_name} />
                      <span>{specie.display_name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Unknown</p>
            )}
            <div className={styles.otherSpecies}>
              {speciesWithoutImagesCount > 0 && (
                <>
                  <span>Other Species:</span>
                  {speciesWithoutImages.map((s, i) => (
                    <Link to={OmniaUrls.species(s)} className={styles.speciesNoImage} key={s.name}>
                      {s.display_name}
                      {speciesWithoutImagesCount !== i + 1 && ','}
                    </Link>
                  ))}
                </>
              )}
            </div>
          </Panel>
          {ambassadors.length > 0 && (
            <Panel
              header={<h3>Who are the top ambassadors for {primary_name}?</h3>}
              key={WaterbodyDetailLearnMorePanelKeys.Ambassadors}
            >
              <div className={styles.ambassadorContainer}>
                {ambassadors.slice(0, ambassadorCount).map((profile) => (
                  <div className={styles.ambassador} key={profile.slug}>
                    <WaterbodyDetailUserProfileCard
                      profile={profile}
                      onClick={() =>
                        WebAnalytics.lakePageClick(
                          '[ambassadors].(user_link)',
                          webAnalyticsLakeClickArgs
                        )
                      }
                    />
                  </div>
                ))}
              </div>
              {ambassadors?.length > ambassadorCount && (
                <div className={styles.seeMoreBtnContainer}>
                  <Button onClick={() => setAmbassadorCount((prev) => prev + 12)} type="link">
                    View More
                  </Button>
                </div>
              )}
            </Panel>
          )}
        </Collapse>
      </div>
    </div>
  );
};
